import {isMobilePhoneNumber} from '@resurs-internal/rb-app-api';
import type {CountryCode} from '@resurs-internal/mui/assets/countries';

export type NordicCountry = 'SE' | 'NO' | 'DK' | 'FI';

/**
 * Single source of truth for the Nordic countries the verify-contact dialog
 * supports — calling code (for E.164) and locale tag (for the phone validator).
 *
 * Ordering matters: longer prefixes must come first so `parseE164` / `isValidPhone`
 * match `+358` before `+46`.
 */
const NORDIC_COUNTRIES = [
  {country: 'FI', callingCode: '+358', locale: 'fi-FI'},
  {country: 'SE', callingCode: '+46', locale: 'sv-SE'},
  {country: 'NO', callingCode: '+47', locale: 'nb-NO'},
  {country: 'DK', callingCode: '+45', locale: 'da-DK'},
] as const;

export const callingCodeForCountry = (country: CountryCode): string =>
  NORDIC_COUNTRIES.find((c) => c.country === country)?.callingCode ?? '+46';

/** Parse an E.164 number into the dropdown country + local digits. */
export const parseE164 = (
  value: string,
): {
  country: NordicCountry;
  callingCode: string;
  localNumber: string;
} | null => {
  const trimmed = value.replace(/[\s-]/g, '');
  const match = NORDIC_COUNTRIES.find((c) => trimmed.startsWith(c.callingCode));
  if (!match) return null;
  return {
    country: match.country,
    callingCode: match.callingCode,
    localNumber: trimmed.slice(match.callingCode.length),
  };
};

/**
 * Combine a calling code with a local number into E.164. Strips leading zeros
 * and any non-digits from the local part; returns `''` when there are no
 * digits left (so callers can use the empty string as "invalid / pending").
 */
export const buildE164 = (callingCode: string, localNumber: string): string => {
  const digits = localNumber.replace(/[^0-9]/g, '').replace(/^0+/, '');
  return digits ? `${callingCode}${digits}` : '';
};

/**
 * Whether an E.164 phone number is a valid mobile number for its country.
 * Returns false for anything outside the supported Nordic calling codes.
 */
export const isValidPhone = (value: string): boolean => {
  const match = NORDIC_COUNTRIES.find((c) => value.startsWith(c.callingCode));
  return match ? isMobilePhoneNumber(value, match.locale) : false;
};
